import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faUser,
  faEnvelope,
  faPhone,
  faBuilding,
  faCalendarAlt,
  faUserShield,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const userData = await userService.getUserById(userId);
        setUser(userData);
        
        // Fetch company details if user has a company
        if (userData.companyId) {
          const companyData = await companyService.getCompanyById(userData.companyId);
          setCompany(companyData);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete user "${user?.name}"? This action cannot be undone.`)) {
      // Handle delete logic
      navigate('/admin/users');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faUser} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          User not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/admin/users')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Users
          </Button>
          <div>
            <h2 className="mb-1">{user.name}</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faUser} className="me-1" />
              {user.role === 'admin' ? 'System Administrator' : 'Property Manager'}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="primary"
            onClick={() => navigate(`/admin/users/${userId}/edit`)}
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit User
          </Button>
          <Button 
            variant="outline-danger" 
            onClick={handleDelete}
            disabled={user.role === 'admin'}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete User
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* User Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">User Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Full Name</label>
                    <p className="mb-0 fw-bold">{user.name}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Role</label>
                    <div>
                      <Badge bg={user.role === 'admin' ? 'danger' : 'info'}>
                        {user.role === 'admin' ? 'Admin' : 'Manager'}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      Email
                    </label>
                    <p className="mb-0">{user.email}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faPhone} className="me-1" />
                      Phone
                    </label>
                    <p className="mb-0">{user.phone || 'Not specified'}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faBuilding} className="me-1" />
                      Company
                    </label>
                    <p className="mb-0">
                      {company ? company.name : (user.role === 'admin' ? 'System Admin' : 'Not assigned')}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Status</label>
                    <div>
                      <Badge bg={user.status === 'active' ? 'success' : 'secondary'}>
                        {user.status}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      Created Date
                    </label>
                    <p className="mb-0">
                      {user.createdAt ? 
                        new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 
                        'Not specified'
                      }
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      Last Updated
                    </label>
                    <p className="mb-0">
                      {user.updatedAt ? 
                        new Date(user.updatedAt.seconds * 1000).toLocaleDateString() : 
                        'Not specified'
                      }
                    </p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary"
                  onClick={() => navigate(`/admin/users/${userId}/edit`)}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit User
                </Button>
                {user.role !== 'admin' && (
                  <Button 
                    variant="outline-danger"
                    onClick={handleDelete}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete User
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* User Statistics */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">User Statistics</h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <div className="mb-3">
                  <FontAwesomeIcon 
                    icon={user.role === 'admin' ? faUserShield : faUsers} 
                    className="text-primary mb-2" 
                    size="2x" 
                  />
                  <h4 className="mb-1">{user.role === 'admin' ? 'Admin' : 'Manager'}</h4>
                  <p className="text-muted mb-0">User Role</p>
                </div>
                <hr />
                <div className="text-center">
                  <h4 className="mb-1">{user.status === 'active' ? 'Active' : 'Inactive'}</h4>
                  <p className="text-muted mb-0">Account Status</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDetails; 